import React, { useEffect, useState } from "react";
import axios from "axios";
import { Plus, Edit2, Trash2, X, Check, Award, ExternalLink, Copy, Trash, Star } from "lucide-react";
import ImageUpload from "../../components/ImageUpload";
import MarkdownEditor from "../../components/MarkdownEditor";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const inputStyle = { background: "white", border: "1px solid #e5e7eb", borderRadius: "4px", padding: "10px 12px", fontFamily: "Manrope", fontSize: "14px", outline: "none", width: "100%", color: "#0A0A0A" };
const labelStyle = { fontFamily: "Manrope", fontSize: "11px", fontWeight: 700, color: "#6b7280", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "4px", display: "block" };

function BrandForm({ brand, onSave, onClose }) {
  const initial = brand || {
    name: "", slug: "", short_description: "", long_description: "",
    country: "Италия", founded_year: "",
    facts: [{ title: "", description: "" }],
    factory_images: [], certificates: [], logo_url: "", cover_image: "",
    is_active: true, is_featured: false, sort_order: 0,
  };
  const [form, setForm] = useState({
    ...initial,
    facts: (initial.facts && initial.facts.length > 0) ? initial.facts : [{ title: "", description: "" }],
  });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const updateFact = (idx, field, value) => {
    const next = [...form.facts];
    next[idx] = { ...next[idx], [field]: value };
    set("facts", next);
  };
  const addFact = () => set("facts", [...form.facts, { title: "", description: "" }]);
  const removeFact = (idx) => set("facts", form.facts.filter((_, i) => i !== idx));

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setLoading(true);
    try {
      const payload = {
        ...form,
        facts: form.facts.filter(f => f.title?.trim()),
        founded_year: form.founded_year ? parseInt(form.founded_year) : null,
      };
      if (brand?.id) {
        await axios.put(`${API}/brands/${brand.id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API}/brands`, payload, { withCredentials: true });
      }
      onSave();
    } catch (e) {
      alert(e?.response?.data?.detail || "Ошибка сохранения");
    } finally { setLoading(false); }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 100, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "20px", overflowY: "auto" }}>
      <div className="light-section" style={{ background: "white", borderRadius: "12px", width: "100%", maxWidth: "720px", padding: "24px", marginTop: "20px", marginBottom: "20px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
          <h3 style={{ fontFamily: "Cormorant Garamond", fontSize: "24px", fontWeight: 500, margin: 0 }}>{brand ? "Редактировать бренд" : "Новый бренд"}</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#888" }}><X size={20} /></button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px" }}>
          <div style={{ gridColumn: "1/-1" }}>
            <label style={labelStyle}>Название бренда</label>
            <input data-testid="brand-name-input" value={form.name} onChange={e => set("name", e.target.value)} style={inputStyle} placeholder="Molto Caldo" />
          </div>
          <div>
            <label style={labelStyle}>Страна</label>
            <input value={form.country || ""} onChange={e => set("country", e.target.value)} style={inputStyle} placeholder="Италия" />
          </div>
          <div>
            <label style={labelStyle}>Год основания</label>
            <input type="number" value={form.founded_year || ""} onChange={e => set("founded_year", e.target.value)} style={inputStyle} placeholder="1957" />
          </div>
          <div style={{ gridColumn: "1/-1" }}>
            <label style={labelStyle}>Короткое описание (для карточки в каталоге)</label>
            <input data-testid="brand-short-desc" value={form.short_description} onChange={e => set("short_description", e.target.value)} style={inputStyle} placeholder="Итальянский бренд премиальных пуховиков и пальто" maxLength={120} />
          </div>
          <div style={{ gridColumn: "1/-1" }}>
            <label style={labelStyle}>Полное описание</label>
            <MarkdownEditor
              value={form.long_description}
              onChange={v => set("long_description", v)}
              rows={5}
              testId="brand-long-desc"
              placeholder="Семейное ателье из Милана, основанное в 1957..."
            />
          </div>

          <div style={{ gridColumn: "1/-1" }}>
            <ImageUpload
              value={form.cover_image ? [form.cover_image] : []}
              onChange={imgs => set("cover_image", imgs[0] || "")}
              maxImages={1}
              testId="brand-cover"
              label="Обложка / главное фото"
            />
          </div>

          {/* Facts */}
          <div style={{ gridColumn: "1/-1" }}>
            <label style={labelStyle}>Преимущества бренда (3-5 фактов)</label>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {form.facts.map((fact, idx) => (
                <div key={idx} data-testid={`brand-fact-${idx}`} style={{ background: "#FDFBF7", border: "1px solid #e5e7eb", borderRadius: "6px", padding: "10px", display: "grid", gridTemplateColumns: "auto 1fr 1fr auto", gap: "8px", alignItems: "center" }}>
                  <div style={{ width: "26px", height: "26px", borderRadius: "50%", background: "linear-gradient(135deg,#C5A059,#D4B878)", color: "#0A0A0A", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Cormorant Garamond", fontWeight: 700, fontSize: "13px" }}>{idx + 1}</div>
                  <input value={fact.title} onChange={e => updateFact(idx, "title", e.target.value)} style={{ ...inputStyle, padding: "8px 10px", fontSize: "13px" }} placeholder="Преимущество (заголовок)" />
                  <input value={fact.description} onChange={e => updateFact(idx, "description", e.target.value)} style={{ ...inputStyle, padding: "8px 10px", fontSize: "13px" }} placeholder="Краткое пояснение" />
                  <button type="button" onClick={() => removeFact(idx)} style={{ background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: "4px", padding: "6px", cursor: "pointer", color: "#dc2626" }}>
                    <Trash size={12} />
                  </button>
                </div>
              ))}
              <button type="button" onClick={addFact} data-testid="add-fact-btn"
                style={{ alignSelf: "flex-start", background: "white", border: "1px dashed #C5A059", color: "#C5A059", borderRadius: "6px", padding: "6px 12px", cursor: "pointer", fontFamily: "Manrope", fontSize: "12px", display: "flex", alignItems: "center", gap: "4px" }}>
                <Plus size={12} /> Добавить факт
              </button>
            </div>
          </div>

          {/* Factory gallery */}
          <div style={{ gridColumn: "1/-1" }}>
            <ImageUpload
              value={form.factory_images}
              onChange={imgs => set("factory_images", imgs)}
              maxImages={12}
              testId="brand-factory"
              label="Фотографии фабрики / производства"
            />
          </div>

          {/* Certificates */}
          <div style={{ gridColumn: "1/-1" }}>
            <ImageUpload
              value={form.certificates}
              onChange={imgs => set("certificates", imgs)}
              maxImages={8}
              testId="brand-certificates"
              label="Сертификаты и документы об официальном сотрудничестве"
            />
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <input type="checkbox" id="brand_featured" checked={form.is_featured} onChange={e => set("is_featured", e.target.checked)} />
            <label htmlFor="brand_featured" style={{ fontFamily: "Manrope", fontSize: "13px" }}>Featured (на главной)</label>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <input type="checkbox" id="brand_active" checked={form.is_active} onChange={e => set("is_active", e.target.checked)} />
            <label htmlFor="brand_active" style={{ fontFamily: "Manrope", fontSize: "13px" }}>Активен</label>
          </div>
        </div>

        <div style={{ display: "flex", gap: "10px", marginTop: "24px" }}>
          <button onClick={onClose} style={{ flex: 1, background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: "6px", padding: "12px", cursor: "pointer", fontFamily: "Manrope", fontSize: "13px" }}>Отмена</button>
          <button data-testid="save-brand-btn" onClick={handleSave} disabled={loading || !form.name.trim()}
            style={{ flex: 2, background: "linear-gradient(135deg,#C5A059,#D4B878)", color: "#0A0A0A", border: "none", borderRadius: "6px", padding: "12px", cursor: "pointer", fontFamily: "Manrope", fontWeight: 700, fontSize: "13px", display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}>
            <Check size={15} />{loading ? "Сохранение..." : "Сохранить бренд"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminBrands() {
  const [brands, setBrands] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/brands?active_only=false`, { withCredentials: true });
      setBrands(r.data || []);
    } catch { /* noop */ }
  };
  useEffect(() => { load(); }, []);

  const handleDelete = async (id, name, productsCount = 0) => {
    const warn = productsCount
      ? `\n\n${productsCount} товар(ов) этого бренда останутся в каталоге без бренда — найти их можно в поиске товаров фильтром «Без бренда».`
      : "";
    if (!window.confirm(`Удалить бренд «${name}»?${warn}`)) return;
    try {
      const r = await axios.delete(`${API}/brands/${id}`, { withCredentials: true });
      const detached = r.data?.detached_products || 0;
      await load();
      if (detached) alert(`Бренд «${name}» удалён. Товаров без бренда: ${detached}.`);
    } catch (e) {
      alert("Не удалось удалить бренд: " + (e.response?.data?.detail || e.message));
    }
  };

  const copyLink = (slug) => {
    const url = `${window.location.origin}/app/brand/${slug}`;
    navigator.clipboard?.writeText(url);
    alert("Ссылка скопирована:\n" + url);
  };

  return (
    <div className="light-section" data-testid="admin-brands-page" style={{ maxWidth: "1000px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <Award size={22} style={{ color: "#C5A059" }} />
          <h2 style={{ fontFamily: "Cormorant Garamond", fontSize: "26px", margin: 0, fontWeight: 500 }}>Бренды</h2>
        </div>
        <button data-testid="add-brand-btn" onClick={() => { setEditing(null); setShowForm(true); }}
          style={{ background: "linear-gradient(135deg,#C5A059,#D4B878)", color: "#0A0A0A", border: "none", borderRadius: "6px", padding: "10px 16px", cursor: "pointer", fontFamily: "Manrope", fontWeight: 700, fontSize: "12px", letterSpacing: "0.08em", textTransform: "uppercase", display: "flex", alignItems: "center", gap: "6px" }}>
          <Plus size={14} />Добавить бренд
        </button>
      </div>

      {brands.length === 0 ? (
        <div style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: "10px", padding: "60px 20px", textAlign: "center", color: "#9ca3af", fontFamily: "Manrope" }}>
          Пока ни одного бренда. Добавьте первый — например, Molto Caldo.
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "14px" }}>
          {brands.map(b => (
            <div key={b.id} data-testid={`brand-card-${b.slug}`} style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: "10px", overflow: "hidden", display: "flex", flexDirection: "column" }}>
              {b.cover_image ? (
                <img src={b.cover_image} alt={b.name} style={{ width: "100%", height: "140px", objectFit: "cover" }} />
              ) : (
                <div style={{ width: "100%", height: "100px", background: "linear-gradient(135deg, #FDFBF7, #f3f4f6)", display: "flex", alignItems: "center", justifyContent: "center", color: "#C5A059", fontFamily: "Cormorant Garamond", fontSize: "28px", fontWeight: 500 }}>
                  {b.name}
                </div>
              )}
              <div style={{ padding: "14px", flex: 1, display: "flex", flexDirection: "column" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "6px", marginBottom: "4px" }}>
                  <div>
                    <div style={{ fontFamily: "Cormorant Garamond", fontSize: "19px", fontWeight: 500 }}>{b.name}{b.is_featured && <Star size={12} style={{ marginLeft: 4, color: "#C5A059", verticalAlign: "middle" }} />}</div>
                    <div style={{ fontFamily: "Manrope", fontSize: "11px", color: "#9ca3af" }}>
                      {b.country || ""}{b.founded_year ? ` · с ${b.founded_year}` : ""}
                    </div>
                  </div>
                  {!b.is_active && <span style={{ background: "#f3f4f6", color: "#9ca3af", fontFamily: "Manrope", fontSize: "9px", padding: "2px 6px", borderRadius: "10px", letterSpacing: "0.08em" }}>HIDDEN</span>}
                </div>
                {b.short_description && <div style={{ fontFamily: "Manrope", fontSize: "12px", color: "#6b7280", lineHeight: 1.45, marginBottom: "10px", flex: 1 }}>{b.short_description}</div>}
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "10px" }}>
                  <span style={{ background: "rgba(197,160,89,0.12)", color: "#92400e", fontFamily: "Manrope", fontSize: "10px", padding: "3px 8px", borderRadius: "10px", fontWeight: 600 }}>{b.facts?.length || 0} фактов</span>
                  <span style={{ background: "#FDFBF7", color: "#6b7280", fontFamily: "Manrope", fontSize: "10px", padding: "3px 8px", borderRadius: "10px" }}>{b.factory_images?.length || 0} фото фабрики</span>
                  <span style={{ background: "#FDFBF7", color: "#6b7280", fontFamily: "Manrope", fontSize: "10px", padding: "3px 8px", borderRadius: "10px" }}>{b.certificates?.length || 0} сертификатов</span>
                  <span style={{ background: "#ecfdf5", color: "#065f46", fontFamily: "Manrope", fontSize: "10px", padding: "3px 8px", borderRadius: "10px", fontWeight: 600 }}>{b.products_count || 0} товаров</span>
                </div>

                <div style={{ display: "flex", gap: "6px" }}>
                  <a href={`/app/brand/${b.slug}`} target="_blank" rel="noreferrer"
                    style={{ flex: 1, background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: "4px", padding: "7px 8px", cursor: "pointer", color: "#374151", display: "flex", alignItems: "center", justifyContent: "center", gap: "4px", fontFamily: "Manrope", fontSize: "11px", textDecoration: "none" }}>
                    <ExternalLink size={11} /> Открыть
                  </a>
                  <button onClick={() => copyLink(b.slug)} title="Копировать ссылку"
                    style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: "4px", padding: "7px 10px", cursor: "pointer", color: "#374151" }}>
                    <Copy size={12} />
                  </button>
                  <button onClick={() => { setEditing(b); setShowForm(true); }} style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: "4px", padding: "7px 10px", cursor: "pointer", color: "#374151" }}><Edit2 size={12} /></button>
                  <button onClick={() => handleDelete(b.id, b.name, b.products_count)} style={{ background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: "4px", padding: "7px 10px", cursor: "pointer", color: "#dc2626" }}><Trash2 size={12} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && <BrandForm brand={editing} onSave={() => { setShowForm(false); load(); }} onClose={() => setShowForm(false)} />}
    </div>
  );
}
